import React from 'react';
import { Save } from 'lucide-react';
import type { ZoneDefinition } from '@/types';
import type { TranslationKeys } from '@/core/i18n/translations';
import type { ZoneValidationResult } from '@/shared/utils/zoneValidation';
import type { UnitLabelKey } from '@/types';

interface ZonesDisplayProps {
  zones: ZoneDefinition[];
  onZoneChange: (id: string, field: 'min' | 'max', value: number) => void;
  title: string;
  language: 'en' | 'pl';
  t: TranslationKeys;
  /** Translation key for the "Wartości w …" caption above the inputs. */
  unitLabelKey: UnitLabelKey;
  /** Default-author method label OR null when the user has edited the table (shows "Custom" badge). */
  methodLabel: string | null;
  /** Whether the current table is a user-customised one (controls the badge variant). */
  isCustom: boolean;
  /** Real-time validation outcome — flips inputs red and feeds the inline error message. */
  validation: ZoneValidationResult;
  /** Save handler — invoked when the user clicks "Zapisz moje strefy". */
  onSave?: () => void;
  /** Whether the user is currently logged in (gates the save button entirely). */
  canSave: boolean;
  /** True while the save round-trip is in flight (disables the button + swaps the label). */
  saving: boolean;
}

const ZonesDisplay: React.FC<ZonesDisplayProps> = ({
  zones,
  onZoneChange,
  title,
  language,
  t,
  unitLabelKey,
  methodLabel,
  isCustom,
  validation,
  onSave,
  canSave,
  saving,
}) => {
  // Display tweak: Polish UI uses S1/S2/… labels while the underlying IDs stay Z1/Z2/…
  const formatZoneId = (id: string) => (language === 'pl' ? id.replace('Z', 'S') : id);

  const unitLabel = t[unitLabelKey];
  const badgeText = isCustom ? t.zonesMethodCustom : methodLabel;
  const firstError = Object.values(validation.errors)[0];
  const firstErrorMessage = firstError ? t[firstError.messageKey] : null;

  return (
    <div className="mb-6 space-y-2">
      <div className="flex justify-between items-end mb-2 gap-3 flex-wrap">
        <div className="flex flex-col gap-0.5 min-w-0">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">{title}</h3>
          <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{unitLabel}</span>
        </div>
        {badgeText && (
          <span
            data-testid="zones-method-badge"
            className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${
              isCustom
                ? 'text-orange-700 bg-orange-50 border-orange-200 dark:text-orange-300 dark:bg-orange-900/30 dark:border-orange-700/60'
                : 'text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
            }`}
          >
            {badgeText}
          </span>
        )}
      </div>

      {firstErrorMessage && (
        <div
          data-testid="zones-error"
          className="text-[11px] text-red-700 bg-red-50 dark:text-red-200 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-2 py-1.5 leading-snug"
        >
          {firstErrorMessage}
        </div>
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-2">
        {zones.map((zone) => {
          const zoneError = validation.errors[zone.id];
          const minInvalid = zoneError?.field === 'min';
          const maxInvalid = zoneError?.field === 'max' || zoneError?.field === 'continuity';
          return (
            <div
              key={zone.id}
              className={`flex flex-col items-center p-1.5 rounded text-xs transition-colors ${
                zoneError
                  ? 'bg-red-50/70 dark:bg-red-900/20 border border-red-300 dark:border-red-700'
                  : 'bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 hover:border-slate-300 dark:hover:border-slate-600'
              }`}
            >
              <div className="flex items-center justify-between w-full mb-1">
                <span className="font-bold text-slate-700 dark:text-slate-300" title={zone.name}>
                  {formatZoneId(zone.id)}
                </span>
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: zone.color }} title={zone.name} />
              </div>

              <div className="flex items-center gap-1 w-full mt-1">
                <input
                  type="number"
                  min="0"
                  max="300"
                  className={`no-spinner w-full min-w-0 bg-white dark:bg-slate-900 border rounded px-0.5 py-0.5 text-center text-xs text-slate-800 dark:text-slate-200 focus:outline-none hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors appearance-none ${
                    minInvalid
                      ? 'border-red-500 focus:border-red-600 ring-1 ring-red-200 dark:ring-red-800'
                      : 'border-slate-300 dark:border-slate-700 focus:border-orange-500'
                  }`}
                  value={Math.round(zone.min * 100)}
                  onChange={(e) => {
                    const raw = parseFloat(e.target.value);
                    onZoneChange(zone.id, 'min', Number.isFinite(raw) ? raw / 100 : 0);
                  }}
                  aria-invalid={minInvalid || undefined}
                  data-testid={`zone-${zone.id}-min`}
                />
                <span className="text-slate-400 dark:text-slate-500">-</span>
                <input
                  type="number"
                  min="0"
                  max="300"
                  className={`no-spinner w-full min-w-0 bg-white dark:bg-slate-900 border rounded px-0.5 py-0.5 text-center text-xs text-slate-800 dark:text-slate-200 focus:outline-none hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors appearance-none ${
                    maxInvalid
                      ? 'border-red-500 focus:border-red-600 ring-1 ring-red-200 dark:ring-red-800'
                      : 'border-slate-300 dark:border-slate-700 focus:border-orange-500'
                  }`}
                  value={Math.round(zone.max * 100)}
                  onChange={(e) => {
                    const raw = parseFloat(e.target.value);
                    onZoneChange(zone.id, 'max', Number.isFinite(raw) ? raw / 100 : 0);
                  }}
                  aria-invalid={maxInvalid || undefined}
                  data-testid={`zone-${zone.id}-max`}
                />
              </div>
            </div>
          );
        })}
      </div>

      {onSave && (
        <div className="flex items-center justify-end pt-1">
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave || saving}
            data-testid="zones-save-btn"
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${
              !canSave || saving
                ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700 cursor-not-allowed'
                : 'bg-orange-600 text-white border-orange-600 hover:bg-orange-500 hover:border-orange-500'
            }`}
            title={!canSave && !saving ? t.zonesLoginToSave : undefined}
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? t.zonesSaving : t.zonesSaveBtn}
          </button>
        </div>
      )}
    </div>
  );
};

export default ZonesDisplay;
